// src/components/layout/AppShell.tsx — SCE Issues
// Layout global : Header + contenu scrollable + BottomNav
//
// ─── Lot 7 — bascule de rôle ───
// Le bandeau hachuré de la vue active n'est plus affiché (présentation du
// 08/10). Le composant BandeauVueRole reste dans features/vue-role.

import type { ReactNode } from 'react'
import { BottomNav } from './BottomNav'

export interface AppShellProps {
  header: ReactNode
  children: ReactNode
  /** Masque la BottomNav (ex: pages modales / formulaire plein écran) */
  hideBottomNav?: boolean
  /**
   * Largeur maximale du contenu.
   *  - 'content' (défaut) : 760px — listes, formulaires, fiches
   *  - 'wide'             : 1100px — dashboard, les graphiques ont besoin d'air
   *  - 'large'            : 1680px — accueil, trois colonnes sur grand écran
   *  - 'full'             : aucun plafond (pages outillage : /debug)
   */
  width?: LargeurPage
}

export type LargeurPage = 'content' | 'wide' | 'large' | 'full'

export const LARGEURS_PAGE: Record<LargeurPage, string> = {
  content: 'var(--width-content)',
  wide: 'var(--width-wide)',
  large: 'var(--width-large)',
  full: 'none',
}
const LARGEURS = LARGEURS_PAGE

/**
 * Shell principal de l'application.
 * Gère la stickiness du header, le scroll du contenu, et le padding bottom
 * pour éviter le chevauchement avec la BottomNav (68px + safe-area).
 *
 * Ordre vertical : Header → main → BottomNav
 */
export function AppShell({
  header,
  children,
  hideBottomNav,
  width = 'content',
}: AppShellProps): JSX.Element {
  return (
    // ─── Un seul plan pour toute l'application ───
    //
    // Une prop `background` permettait de choisir entre blanc et gris 5 :
    // quatre pages prenaient le gris, les autres le blanc. Le plan étant
    // désormais le même partout (cf. globals.css), ce choix n'avait plus
    // d'objet — un réglage qui ne règle rien finit par être recopié au hasard
    // sur les pages suivantes.
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--color-bg-primary)' }}
    >
      {/*
        Bandeau et barre de titre collés ENSEMBLE : deux blocs collants à
        top-0 se recouvriraient au défilement. Sans vue active, le bloc ne
        contient que le Header — comportement inchangé.
      */}
      <div className="sticky top-0 z-40">
        {/* Bandeau hachuré retiré de l'affichage (présentation du 08/10) ;
            on revient à sa vue par le menu du compte. */}
        {header}
      </div>

      {/*
        Le plafond de largeur vit ici plutôt que dans chaque page : une seule
        modification et tout l'applicatif suit. `width: 100%` + `margin auto`
        centrent sans casser le comportement mobile (le plafond ne mord qu'au
        delà de sa propre valeur).
      */}
      <main
        className="flex-1 w-full"
        style={{
          maxWidth: LARGEURS[width],
          marginInline: 'auto',
          paddingBottom: hideBottomNav ? 16 : 'calc(68px + env(safe-area-inset-bottom, 0))',
        }}
      >
        {children}
      </main>

      {!hideBottomNav && <BottomNav />}
    </div>
  )
}
